import { useClaims } from '@backoffice-frontend/authentication'
import { recordRecentlyVisitedAreaId } from '@backoffice-frontend/common'
import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import type { BackofficeAreas } from './types/BackofficeArea'

const isPathInArea = (pathname: string, areaPath: string): boolean =>
  pathname === areaPath || pathname.startsWith(`${areaPath}/`)

/**
 * Records the area matching the current route as "recently visited". Runs at
 * the shell level so it captures navigation from anywhere — links, the command
 * palette, browser back/forward. Persistence is purely client-side; see
 * `recordRecentlyVisitedAreaId`.
 *
 * Only the Backoffice app surfaces recently visited areas (on its welcome
 * page), so tracking is opt-in via `enabled` to avoid recording in the Admin
 * Panel, which shares this shell.
 */
export const useTrackRecentlyVisitedAreas = (
  areas: BackofficeAreas,
  enabled: boolean,
): void => {
  const { pathname } = useLocation()
  // Scope the history to the logged-in user (shared devices).
  const userId = useClaims().cognito.username()

  // The longest matching route path wins, so a nested area beats the "/"
  // redirect and any shorter prefix.
  const matchedAreaId = useMemo(
    () =>
      areas
        .filter(area => isPathInArea(pathname, area.route.path))
        .sort((a, b) => b.route.path.length - a.route.path.length)[0]?.id,
    [pathname, areas],
  )

  useEffect(() => {
    if (enabled && userId && matchedAreaId) {
      recordRecentlyVisitedAreaId(userId, matchedAreaId)
    }
  }, [enabled, userId, matchedAreaId])
}
